import { defineStore } from 'pinia';
import type { ErpStoreDetails } from './stores';
import type { Store, StoreLocation } from '@/types/store';
import type {
  ContentfulContentListingPage,
  ContentfulLogo,
  AlertBanner,
  ContentPage,
  ContentPageCollection,
  InformationBanner,
  StorePage,
  StorePageCollection,
  StoreDetails,
  RetailerWithSysId,
} from '@/types/contentful';
import gql from '@/gql/contentful';

type FooterStoreDetails = {
  id: string;
  name?: string;
  address?: string;
  phone?: string;
  openingHoursMondays?: string;
  openingHoursTuesdays?: string;
  openingHoursWednesdays?: string;
  openingHoursThursdays?: string;
  openingHoursFridays?: string;
  openingHoursSaturdays?: string;
  openingHoursSundays?: string;
  email?: string;
  location?: {
    lat: number;
    lon: number;
  };
};

type StoreDetailsCollection = {
  storePageCollection: {
    items: StoreDetails[];
  };
};

type RetailerCollection = {
  retailerCollection: {
    items: RetailerWithSysId[];
  };
};

type ErrorWithMessage = { message?: string } | null;

export const useContentStore = defineStore('content', () => {
  const hasAllContentListingPages = ref(false);
  const contentListingPages = ref<{ [key: string]: ContentfulContentListingPage }>({});
  const logos = ref<ContentfulLogo | undefined>(undefined);
  const footerStoreDetails = ref<FooterStoreDetails | null>(null);
  const storesDetails = ref<{ [key: string]: StoreDetails }>({});
  const allStoresDetailsCached = ref(false);
  const storePages = ref<{ [key: string]: StorePage }>({});
  const retailers = ref<RetailerWithSysId[]>([]);
  const contentPages = ref<{ [key: string]: ContentPage }>({});
  const informationBanner = ref<InformationBanner | null>(null);
  const alertBanner = ref<AlertBanner | null>(null);
  const informationBannerActive = ref(false);
  const alertBannerActive = ref(false);
  const retailersCached = ref(false);

  const getAllContentListingPages = async (): Promise<{ [key: string]: ContentfulContentListingPage }> => {
    const { fetchEntries } = useContentful();
    if (hasAllContentListingPages.value) return contentListingPages.value;
    const { data } = await fetchEntries({
      contentType: 'contentListingPage',
      include: 1,
    });
    for (const contentListingPage of data) {
      contentListingPages.value[contentListingPage.fields.slug] = contentListingPage as ContentfulContentListingPage;
    }
    hasAllContentListingPages.value = true;
    return contentListingPages.value;
  };

  const getContentListingPage = async (slug: string): Promise<ContentfulContentListingPage> => {
    const { fetchEntry } = useContentful();
    if (contentListingPages.value[slug]) return contentListingPages.value[slug];
    const { data } = await fetchEntry({
      contentType: 'contentListingPage',
      include: 1,
      'fields.slug': slug,
    });

    contentListingPages.value[slug] = data as ContentfulContentListingPage;
    return data;
  };

  const getContentPage = async (
    slug: string,
    parentSlug: string,
  ): Promise<{ data?: ContentPage | null; error: ErrorWithMessage }> => {
    const key = `${parentSlug}_${slug}`;

    if (contentPages.value[key]) {
      return { data: contentPages.value[key], error: null };
    }

    const { fetchEntryWithGraphQL } = useContentful();
    const { data, error } = await fetchEntryWithGraphQL<ContentPageCollection>(gql.getContentPage, { slug });

    if ((Array.isArray(error) && error.length) || (!Array.isArray(error) && error)) {
      return { error, data: null };
    }

    if (!data || !data?.contentPageCollection?.items || !data.contentPageCollection.items.length) {
      return { data: null, error: { message: `No content page found for slug: ${slug} ` } };
    }

    const contentPage = data.contentPageCollection.items[0];

    contentPages.value[key] = contentPage;

    return { data: contentPage, error: null };
  };

  const fetchLogo = async (): Promise<void> => {
    const { fetchEntry } = useContentful();
    const logoEntry = await fetchEntry({
      contentType: 'logo',
      include: 1,
      select: 'fields.logo,fields.logoSmall,fields.favicon,fields.lightLogo,fields.lightLogoSmall',
    });

    logos.value = logoEntry.data?.fields;
  };

  const fetchAlertBanner = async (): Promise<void> => {
    const { fetchEntry } = useContentful();
    const alertBannerEntry = await fetchEntry({
      contentType: 'alertBanner',
      include: 1,
      select:
        'fields.active,fields.type,fields.message,fields.title,fields.linkUrl,fields.linkText,fields.secondLinkUrl,fields.secondLinkText,fields.hideWhenLoggedIn',
    });

    if (alertBannerEntry?.data?.fields) {
      alertBanner.value = alertBannerEntry.data?.fields;
      alertBannerActive.value = alertBannerEntry.data?.fields?.active;
    }
  };

  const fetchInformationBanner = async (): Promise<void> => {
    const { fetchEntry } = useContentful();
    const informationBannerEntry = await fetchEntry({
      contentType: 'informationBanner',
      include: 1,
      select: 'fields.active,fields.message,fields.linkUrl,fields.linkText,fields.linkIcon',
    });

    if (informationBannerEntry?.data?.fields) {
      informationBanner.value = informationBannerEntry.data?.fields;
      informationBannerActive.value = informationBannerEntry.data?.fields?.active;
    }
  };

  const getStoreDetails = async ({
    store,
  }: {
    store: Store | StoreLocation | ErpStoreDetails;
  }): Promise<{
    error?: unknown;
    storeDetails?: StoreDetails;
    hasContentfulEntry?: boolean;
    data?: StoreDetails[];
  }> => {
    const nuxtApp = useNuxtApp();
    const { fetchEntryWithGraphQL } = useContentful();

    const cached = storesDetails.value[store.id];
    if (cached) return { storeDetails: cached, hasContentfulEntry: true };

    const { data, error } = await nuxtApp.runWithContext(() =>
      fetchEntryWithGraphQL<StoreDetailsCollection>(gql.getStoreDetails, { id: store.id }),
    );

    if ((Array.isArray(error) && error.length) || (!Array.isArray(error) && error)) {
      return { error, storeDetails: undefined };
    }

    const contentStoreDetails =
      data?.storePageCollection?.items.length === 1 ? data.storePageCollection.items[0] : undefined;

    if (contentStoreDetails) {
      storesDetails.value[store.id] = contentStoreDetails;
    }
    return { storeDetails: contentStoreDetails, hasContentfulEntry: !!contentStoreDetails };
  };

  const getAllStoresDetails = async (): Promise<{ data?: StoreDetails[]; error?: ErrorWithMessage }> => {
    const nuxtApp = useNuxtApp();
    const { fetchEntryWithGraphQL } = useContentful();

    if (allStoresDetailsCached.value) {
      const data = Object.keys(storesDetails.value).map((key) => storesDetails.value[key]);
      return { data };
    }

    const { data, error } = await nuxtApp.runWithContext(() =>
      fetchEntryWithGraphQL<StoreDetailsCollection>(gql.getAllStoreDetails, {}),
    );

    if ((Array.isArray(error) && error.length) || (!Array.isArray(error) && error)) {
      return { error };
    }

    if (!data || !data.storePageCollection || !data.storePageCollection.items.length) {
      return { error: { message: 'No store details was found' } };
    }

    data.storePageCollection.items.forEach((store) => {
      storesDetails.value[store.storeId] = store;
    });

    allStoresDetailsCached.value = true;

    return { data: data.storePageCollection.items };
  };

  const getAllRetailers = async (): Promise<{ data?: RetailerWithSysId[]; error?: ErrorWithMessage }> => {
    const nuxtApp = useNuxtApp();
    const { fetchEntryWithGraphQL } = useContentful();

    if (retailersCached.value) {
      return { data: retailers.value, error: undefined };
    }

    const { data, error } = await nuxtApp.runWithContext(() =>
      fetchEntryWithGraphQL<RetailerCollection>(gql.getAllRetailers, {}),
    );

    if ((Array.isArray(error) && error.length) || (!Array.isArray(error) && error)) {
      return { data: undefined, error };
    }

    if (!data || !data.retailerCollection) {
      return { data: [], error: { message: 'No retailers found' } };
    }

    retailersCached.value = true;
    retailers.value = data.retailerCollection.items;

    return { data: data.retailerCollection.items, error: null };
  };

  const getStorePage = async ({
    storeId,
  }: {
    storeId: string;
  }): Promise<{ data: StorePage | null; error?: ErrorWithMessage }> => {
    const nuxtApp = useNuxtApp();
    const { fetchEntryWithGraphQL } = useContentful();

    const cached = storePages.value[storeId];
    if (cached) {
      return { data: cached, error: null };
    }

    const { data, error } = await nuxtApp.runWithContext(() =>
      fetchEntryWithGraphQL<StorePageCollection>(gql.getStorePage, { id: storeId }),
    );

    if ((Array.isArray(error) && error.length) || (!Array.isArray(error) && error)) {
      return { error, data: null };
    }

    if (!data || !data.storePageCollection || !data.storePageCollection.items.length) {
      return { data: null, error: { message: `No store page found for id: ${storeId} ` } };
    }

    const storePage = data.storePageCollection.items[0];

    storePages.value[storeId] = storePage;

    return { data: storePage, error: null };
  };

  const fetchFooterStoreDetails = async ({ store }: { store: Store | ErpStoreDetails }): Promise<void> => {
    const { storeDetails } = await getStoreDetails({ store });

    const parsedStoreDetails = {
      id: store.id,
      name: storeDetails?.name || store?.name,
      address: storeDetails?.address || `${store?.address}, ${store?.postalCode} ${store?.city}`,
      phone: storeDetails?.phone || store?.phone,
      openingHoursMondays: storeDetails?.openingHoursMondays,
      openingHoursTuesdays: storeDetails?.openingHoursTuesdays,
      openingHoursWednesdays: storeDetails?.openingHoursWednesdays,
      openingHoursThursdays: storeDetails?.openingHoursThursdays,
      openingHoursFridays: storeDetails?.openingHoursFridays,
      openingHoursSaturdays: storeDetails?.openingHoursSaturdays,
      openingHoursSundays: storeDetails?.openingHoursSundays,
      email: storeDetails?.email || store?.email,
      location: storeDetails?.location,
    };

    footerStoreDetails.value = parsedStoreDetails;
  };

  const closeInformationBanner = (): void => {
    informationBannerActive.value = false;
  };

  const closeAlertBanner = (): void => {
    alertBannerActive.value = false;
  };

  return {
    logos,
    hasAllContentListingPages,
    contentListingPages,
    footerStoreDetails,
    getContentPage,
    getAllContentListingPages,
    getContentListingPage,
    fetchLogo,
    getStoreDetails,
    getAllStoresDetails,
    getAllRetailers,
    getStorePage,
    fetchFooterStoreDetails,
    informationBanner,
    informationBannerActive,
    closeInformationBanner,
    alertBanner,
    alertBannerActive,
    closeAlertBanner,
    fetchInformationBanner,
    fetchAlertBanner,
  };
});
